import { Props as EqualizerProps } from "./Equalizer";
import { useCallback, useEffect, useRef } from "react";
import { freqCount, gainCount } from "./constants";
import { select, Selection } from "d3";
import {
  EQAllowedFrequencies,
  EQAllowedGains,
} from "shared/games/mixpuzzles/types";
import {
  drawCircle,
  drawGraphBars,
  drawGraphLine,
  drawGraphLines,
  handleClick,
} from "./drawGraphHelpers";
import classNames from "classnames";
import { allowedFreqAndGainToNormalizedFreqAndGain } from "./converters";

interface Props extends EqualizerProps {
  width: number;
  height: number;
}

interface InternalValue {
  gain: EQAllowedGains;
  freq: EQAllowedFrequencies;
}

export interface GraphDrawingParams extends Props {
  svg: Selection<SVGSVGElement | null, unknown, null, unknown>;
  width: number;
  height: number;
  freqResolution: number;
  gainResolution: number;
  gainCount: number;
  freqCount: number;
  circleRadius: number;
  setValue: (params: InternalValue) => void;
  normalizedAnswer: { gain: number; freq: number } | undefined;
}

export default function EqualizerGraph(props: Props) {
  const {
    value,
    onChange,
    height,
    width,
    readOnly,
    roundedClassName = "rounded",
  } = props;
  const d3Ref = useRef<SVGSVGElement>(null);
  const lastCall = useRef({ eq_freq: value.eq_freq, eq_gain: value.eq_gain });
  const optimizedSetValue = useCallback(
    (value: InternalValue) => {
      if (readOnly) {
        return;
      }

      if (
        value.freq === lastCall.current.eq_freq &&
        value.gain === lastCall.current.eq_gain
      ) {
        return;
      }

      lastCall.current.eq_freq = value.freq;
      lastCall.current.eq_gain = value.gain;

      onChange({
        eq_freq: value.freq,
        eq_gain: value.gain,
      });
    },
    [readOnly, onChange]
  );

  useEffect(() => {
    const svg = select(d3Ref.current);
    const gainResolution = height / gainCount;
    const freqResolution = width / freqCount;
    const normalizedAnswer = allowedFreqAndGainToNormalizedFreqAndGain(value);
    const graphProps: GraphDrawingParams = {
      ...props,
      svg,
      width,
      height,
      freqResolution,
      gainResolution,
      freqCount,
      gainCount,
      value,
      setValue: optimizedSetValue,
      circleRadius: 20,
      normalizedAnswer,
    };

    drawGraphBars(graphProps);
    drawGraphLines(graphProps);
    drawCircle(graphProps);
    drawGraphLine(graphProps);
    handleClick(graphProps);
  }, [height, width, value, optimizedSetValue, props]);

  return (
    <svg
      className={classNames(
        "absolute inset-0 ",
        props.readOnly ? "cursor-not-allowed" : "cursor-pointer",
        roundedClassName
      )}
      ref={d3Ref}
      width={width}
      height={height}
    />
  );
}
